import * as React from 'react'
import type { CSSProperties, KeyboardEventHandler } from 'react'
import { useDraggable } from '@dnd-kit/core'
import clsx from 'clsx'

import { GripVerticalIcon, IconButton } from '@buffer-mono/popcorn'

import useDraggingCursor from '~publish/hooks/useDraggingCursor'

import type { CalendarItem } from './types'

import styles from './DraggableItem.module.css'

interface DraggableItemProps extends React.HTMLAttributes<HTMLDivElement> {
  id: CalendarItem['id']
  index: number
  disabled?: boolean
  children: React.ReactNode
  timestamp: CalendarItem['timestamp']
  type: CalendarItem['type']
  onOverlay?: boolean
}

export const DraggableItem = React.memo(
  ({
    id,
    index,
    disabled = false,
    children,
    style,
    className,
    timestamp,
    type,
    onOverlay,
    ...props
  }: DraggableItemProps): JSX.Element => {
    const { attributes, listeners, setNodeRef, transform, isDragging } =
      useDraggable({
        id,
        disabled,
        data: {
          index,
          timestamp,
          type,
        },
      })
    const { onKeyDown, ...mouseListeners } = listeners ?? {}
    useDraggingCursor(isDragging)

    if (isDragging && !onOverlay) {
      return <></>
    }

    if (disabled) {
      return <>{children}</>
    }

    return (
      <div
        {...mouseListeners}
        ref={setNodeRef}
        className={clsx(
          styles.item,
          {
            [styles.isDragging]: onOverlay,
          },
          className,
        )}
        style={
          {
            '--translate-x': transform?.x ? `${transform.x}px` : undefined,
            '--translate-y': transform?.y ? `${transform.y}px` : undefined,
            ...style,
          } as CSSProperties
        }
        {...props}
      >
        <IconButton
          variant="tertiary"
          label="Drag to reschedule"
          className={styles.dragHandle}
          {...attributes}
          onKeyDown={onKeyDown as KeyboardEventHandler}
          data-dnd
          size="small"
        >
          <GripVerticalIcon />
        </IconButton>
        {children}
      </div>
    )
  },
)

DraggableItem.displayName = 'DraggableItem'
